import type {operations} from './generated/openapiV3Internal';

/**
 * HTTP methods used by the internal V3 REST surface. `PUT` is included for completeness even
 * though the current spec does not use it, so the wrapper keeps working if a future operation does.
 */
export type V3InternalHttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

export interface V3InternalRoute {
  method: V3InternalHttpMethod;
  /**
   * The route template, prefix-free (without the `/api/v3-internal` server prefix), exactly as it
   * appears under `paths` in the spec. Path params are written as `{name}` and substituted at call
   * time. Custom-method suffixes (e.g. `:async-custom-method`) are part of the template.
   */
  pathTemplate: string;
}

/**
 * Maps each internal V3 `operationId` to the HTTP method + route template that serves it.
 *
 * The served internal spec is prefix-free, so both the operationIds (the keys here, e.g.
 * `Jellyfish.Create`) and the `pathTemplate` values are un-prefixed. This runtime map supplies the
 * method + path that the generated types can't (types carry no runtime values). The
 * `satisfies Record<keyof operations, V3InternalRoute>` annotation makes it exhaustive: if an
 * operation is added to or removed from the regenerated types, this file fails to type-check until
 * the mapping is updated, preventing drift between the generated types and the runtime routing.
 */
export const V3_INTERNAL_ROUTES = {
  'CreditLimit.Create': {method: 'POST', pathTemplate: '/credit-limit'},
  'CreditLimit.Delete': {method: 'DELETE', pathTemplate: '/credit-limit/{credit_limit_id}'},
  'CreditLimit.Update': {method: 'PATCH', pathTemplate: '/credit-limit/{credit_limit_id}'},
  'CreditLimit.BatchCreate': {method: 'POST', pathTemplate: '/credit-limit:batch-create'},
  'CreditLimit.BatchDelete': {method: 'POST', pathTemplate: '/credit-limit:batch-delete'},
  'CreditLimit.BatchUpdate': {method: 'PATCH', pathTemplate: '/credit-limit:batch-update'},
  'CreditLimit.BulkCreate': {method: 'POST', pathTemplate: '/credit-limit:bulk-create'},
  'CreditLimit.BulkUpdate': {method: 'PATCH', pathTemplate: '/credit-limit:bulk-update'},
  'Jellyfish.Create': {method: 'POST', pathTemplate: '/jellyfish'},
  'Jellyfish.Delete': {method: 'DELETE', pathTemplate: '/jellyfish/{jellyfish_id}'},
  'Jellyfish.Update': {method: 'PATCH', pathTemplate: '/jellyfish/{jellyfish_id}'},
  'Jellyfish.ItemScopedAsyncMethod': {
    method: 'POST',
    pathTemplate: '/jellyfish/{jellyfish_id}:item-scoped-async-method',
  },
  'Jellyfish.ItemScopedSyncMethod': {
    method: 'POST',
    pathTemplate: '/jellyfish/{jellyfish_id}:item-scoped-sync-method',
  },
  'Jellyfish.AsyncCustomMethod': {
    method: 'POST',
    pathTemplate: '/jellyfish:async-custom-method',
  },
  'Jellyfish.BatchCreate': {method: 'POST', pathTemplate: '/jellyfish:batch-create'},
  'Jellyfish.BatchDelete': {method: 'POST', pathTemplate: '/jellyfish:batch-delete'},
  'Jellyfish.BatchUpdate': {method: 'PATCH', pathTemplate: '/jellyfish:batch-update'},
  'Jellyfish.BulkCreate': {method: 'POST', pathTemplate: '/jellyfish:bulk-create'},
  'Jellyfish.BulkCustomMethod': {
    method: 'POST',
    pathTemplate: '/jellyfish:bulk-custom-method',
  },
  'Jellyfish.BulkUpdate': {method: 'PATCH', pathTemplate: '/jellyfish:bulk-update'},
  'Jellyfish.CreateFromYaml': {method: 'POST', pathTemplate: '/jellyfish:create-from-yaml'},
  'Jellyfish.SerializeToYaml': {
    method: 'POST',
    pathTemplate: '/jellyfish:serialize-to-yaml',
  },
  'Jellyfish.SyncCustomMethodBasic': {
    method: 'POST',
    pathTemplate: '/jellyfish:sync-custom-method-basic',
  },
  'Jellyfish.SyncCustomMethodValueObject': {
    method: 'POST',
    pathTemplate: '/jellyfish:sync-custom-method-value-object',
  },
  'User.Create': {method: 'POST', pathTemplate: '/user'},
  'User.Update': {method: 'PATCH', pathTemplate: '/user/{user_id}'},
  'User.BatchCreate': {method: 'POST', pathTemplate: '/user:batch-create'},
  'User.BatchUpdate': {method: 'PATCH', pathTemplate: '/user:batch-update'},
  'User.BulkCreate': {method: 'POST', pathTemplate: '/user:bulk-create'},
  'User.BulkUpdate': {method: 'PATCH', pathTemplate: '/user:bulk-update'},
  'BulkTask.Get': {method: 'GET', pathTemplate: '/tasks/bulk/{task_id}'},
  'BulkErrors.Get': {method: 'GET', pathTemplate: '/tasks/bulk/{task_id}/errors'},
  'BulkSuccessfulResults.Get': {
    method: 'GET',
    pathTemplate: '/tasks/bulk/{task_id}/successful-results',
  },
  'Jellyfish.AsyncCustomMethod.Get': {
    method: 'GET',
    pathTemplate: '/tasks/jellyfish/async-custom-method/{task_id}',
  },
  'Jellyfish.ItemScopedAsyncMethod.Get': {
    method: 'GET',
    pathTemplate: '/tasks/jellyfish/item-scoped-async-method/{task_id}',
  },
} as const satisfies Record<keyof operations, V3InternalRoute>;
